<script setup lang="ts">
import { computed, ref } from "vue";
import type { ExerciseMcq } from "~/features/computer/data/exercises/exercise.types";
import { getMcqConceptLink } from "~/features/computer/data/mcq-links";

const props = defineProps<{
  questions: ExerciseMcq[];
}>();

const index = ref(0);
const selected = ref<number | null>(null);
const score = ref(0);
const showResult = ref(false);

const current = computed(() => props.questions[index.value] ?? null);
const conceptLink = computed(() => (
  current.value ? getMcqConceptLink(current.value) : null
));

function selectOption(optionIndex: number) {
  if (!current.value || selected.value !== null) {
    return;
  }

  selected.value = optionIndex;

  if (optionIndex === current.value.correctIndex) {
    score.value += 1;
  }
}

function next() {
  selected.value = null;

  if (index.value < props.questions.length - 1) {
    index.value += 1;
    return;
  }

  showResult.value = true;
}
</script>

<template>
  <div class="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
    <div v-if="!showResult && current" class="space-y-4">
      <div class="flex items-center justify-between gap-4">
        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Q{{ index + 1 }} / {{ questions.length }}
        </p>
        <span
          v-if="current.difficulty"
          class="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600"
        >
          {{ current.difficulty }}
        </span>
      </div>

      <p class="text-base font-semibold text-slate-900">{{ current.question }}</p>

      <div class="space-y-2">
        <button
          v-for="(option, optionIndex) in current.options"
          :key="`${current.id}-${optionIndex}`"
          type="button"
          class="w-full rounded-2xl border px-4 py-3 text-left text-sm transition"
          :class="selected === null
            ? 'border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50'
            : optionIndex === current.correctIndex
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : selected === optionIndex
                ? 'border-rose-300 bg-rose-50 text-rose-800'
                : 'border-slate-200 bg-white text-slate-500'"
          :disabled="selected !== null"
          @click="selectOption(optionIndex)"
        >
          {{ option }}
        </button>
      </div>

      <div v-if="selected !== null" class="rounded-2xl bg-slate-50 p-4">
        <p class="text-sm font-semibold" :class="selected === current.correctIndex ? 'text-emerald-700' : 'text-rose-700'">
          {{ selected === current.correctIndex ? "Correct" : "Incorrect" }}
        </p>
        <p class="mt-1 text-sm leading-7 text-slate-700">{{ current.explanation }}</p>
        <NuxtLink
          v-if="conceptLink"
          :to="conceptLink"
          class="mt-3 inline-flex text-sm font-medium text-sky-700 underline decoration-sky-300 underline-offset-4 hover:text-sky-900"
        >
          Confused? Jump to explanation ->
        </NuxtLink>
        <button
          type="button"
          class="mt-4 rounded-full bg-slate-950 px-4 py-2 text-sm font-medium text-white"
          @click="next"
        >
          {{ index < questions.length - 1 ? "Next" : "Show score" }}
        </button>
      </div>
    </div>

    <div v-else class="space-y-3 text-center">
      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Quiz Result</p>
      <p class="text-2xl font-bold tracking-tight text-slate-900">Score: {{ score }}/{{ questions.length }}</p>
      <p class="text-sm leading-7 text-slate-600">
        {{ score >= Math.ceil(questions.length * 0.7)
          ? "Core idea samajh aa rahi hai. Ab short aur long answers bhi dekho."
          : "MCQs dobara karo aur phir algorithm aur operators pages revise karo." }}
      </p>
    </div>
  </div>
</template>
